/**
 * Break Management Protected Route (PR 1, Phase B1)
 *
 * Gate for /hr/breaks/* pages. Allows admin + team leaders + manager.
 * Anyone else gets bounced to /dashboard so we don't leak the
 * navigation structure to processors / order-desk / accounting.
 *
 * Mirrors the role list enforced by the backend in `breaks.py`:
 *   admin, team_leader, tax_team_leader, title_team_leader, manager
 *
 * Reuses the same token-validation primitives as AdminProtectedRoute
 * to keep one source of auth truth.
 */

import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const BREAK_MGMT_ALLOWED_ROLES = [
  'admin',
  'team_leader',
  'tax_team_leader',
  'title_team_leader',
  // Real DB role names — discovered during PR 1 sanity check.
  'tax_search_team_leader',
  'title_search_team_leader',
  'manager',
  'hr_manager',
  'hr_specialist',
  'hr',
];

const getUserRole = () => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (parsed?.role || '').toLowerCase();
  } catch {
    return null;
  }
};

const BreakManagementProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const authState = useMemo(() => {
    const token = localStorage.getItem('token');
    if (!token) return { valid: false, reason: 'no_token' };
    const role = getUserRole();
    if (!role || !BREAK_MGMT_ALLOWED_ROLES.includes(role)) {
      return { valid: false, reason: 'not_allowed' };
    }
    return { valid: true, reason: null };
  }, [location.pathname]);

  useEffect(() => {
    if (!authState.valid) {
      if (authState.reason === 'no_token') {
        navigate('/login', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [authState, navigate]);

  if (!authState.valid) return null;
  return children;
};

export default BreakManagementProtectedRoute;
